import { AlertCircle, CheckCircle, Shield, X } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { RoleBadge } from './RoleBadge';

/**
 * Access request interface
 */
interface AccessRequest {
	currentRole: string;
	requiredRole: string;
	action: string;
	reason: string;
}

/**
 * Access Request Modal Props
 */
interface AccessRequestModalProps {
	isOpen: boolean;
	onClose: () => void;
	currentRole: string;
	requiredRole: string;
	action: string;
}

/**
 * Access Request Modal Component
 */
export function AccessRequestModal({
	isOpen,
	onClose,
	currentRole,
	requiredRole,
	action,
}: AccessRequestModalProps) {
	const { user } = useAuth();
	const [reason, setReason] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);

		try {
			// Validate reason
			if (!reason.trim()) {
				throw new Error('Please provide a reason for requesting access');
			}

			const accessRequest: AccessRequest = {
				currentRole,
				requiredRole,
				action,
				reason: reason.trim(),
			};

			// TODO: Replace with actual API call to submit access request
			console.log('Access request submitted:', {
				user: user?.email,
				request: accessRequest,
				timestamp: new Date().toISOString(),
			});

			// Simulate API call
			await new Promise(resolve => setTimeout(resolve, 1000));

			setSuccess(true);

			// Reset form and close after success
			setTimeout(() => {
				setReason('');
				setSuccess(false);
				onClose();
			}, 2000);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to submit access request');
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		if (!isLoading) {
			setReason('');
			setError(null);
			setSuccess(false);
			onClose();
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				{/* Backdrop */}
				<div className="fixed inset-0 transition-opacity" onClick={handleClose}>
					<div className="absolute inset-0 bg-gray-500 opacity-75"></div>
				</div>

				{/* Modal */}
				<div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
					{/* Header */}
					<div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center">
								<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900 sm:mx-0 sm:h-10 sm:w-10">
									<Shield className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
								</div>
								<div className="ml-4">
									<h3 className="text-lg font-medium text-gray-900 dark:text-white">
										Request Access
									</h3>
									<p className="text-sm text-gray-500 dark:text-gray-400">
										Request elevated permissions for this action
									</p>
								</div>
							</div>
							<button
								onClick={handleClose}
								disabled={isLoading}
								className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
							>
								<X className="h-6 w-6" />
							</button>
						</div>

						{/* Success Message */}
						{success && (
							<div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
								<div className="flex items-center">
									<CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
									<span className="text-sm text-green-800 dark:text-green-200">
										Access request submitted successfully!
									</span>
								</div>
							</div>
						)}

						{/* Error Message */}
						{error && (
							<div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
								<div className="flex items-center">
									<AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
									<span className="text-sm text-red-800 dark:text-red-200">
										{error}
									</span>
								</div>
							</div>
						)}

						{/* Request Details */}
						<div className="space-y-4">
							<div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
								<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
									Access Request Details
								</h4>

								<div className="space-y-2 text-sm">
									<div className="flex items-center justify-between">
										<span className="text-gray-600 dark:text-gray-400">
											Action:
										</span>
										<span className="font-medium text-gray-900 dark:text-white">
											{action}
										</span>
									</div>

									<div className="flex items-center justify-between">
										<span className="text-gray-600 dark:text-gray-400">
											Current Role:
										</span>
										<RoleBadge
											role={currentRole}
											size="xs"
											showTooltip={false}
										/>
									</div>

									<div className="flex items-center justify-between">
										<span className="text-gray-600 dark:text-gray-400">
											Required Role:
										</span>
										<RoleBadge
											role={requiredRole}
											size="xs"
											showTooltip={false}
										/>
									</div>

									<div className="flex items-center justify-between">
										<span className="text-gray-600 dark:text-gray-400">
											Requested By:
										</span>
										<span className="font-medium text-gray-900 dark:text-white">
											{user?.email}
										</span>
									</div>
								</div>
							</div>

							{/* Reason Field */}
							<form onSubmit={handleSubmit} className="space-y-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										Reason for Access Request *
									</label>
									<textarea
										required
										value={reason}
										onChange={e => setReason(e.target.value)}
										rows={4}
										className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
										placeholder="Please explain why you need access to this feature. Include details about your use case and how this will help your work."
										disabled={isLoading || success}
									/>
									<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
										Your request will be sent to administrators for review.
									</p>
								</div>
							</form>
						</div>
					</div>

					{/* Footer */}
					<div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
						<button
							type="submit"
							onClick={handleSubmit}
							disabled={isLoading || success || !reason.trim()}
							className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isLoading ? (
								<>
									<svg
										className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Submitting...
								</>
							) : success ? (
								'Submitted!'
							) : (
								'Submit Request'
							)}
						</button>
						<button
							type="button"
							onClick={handleClose}
							disabled={isLoading}
							className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
